<script setup lang="ts">
/**
 * Карточка задачи на доске — макет 5:89: полоса типа 3px слева, шапка с
 * иконкой типа и ключом, название до двух строк, футер с приоритетом, сроком
 * и аватаром исполнителя.
 *
 * Футер собирается из того, что есть: без срока и без исполнителя в макете
 * (10:76) от него остаётся один глиф приоритета, и место под пустые ячейки не
 * держится — карточки в колонке разной высоты, и это нормально.
 *
 * Точки статуса в футере нет, хотя в мастере компонента она есть: на доске
 * статус называет колонка, и в её карточках (10:76, 10:304) точку погасили.
 * Она понадобится на экране списка, где колонок нет, — DEV-1466.
 *
 * Пайплайн-бейдж из макета ждёт источник: `GetBoard` пайплайны не отдаёт, а
 * `ListPipelines` не умеет фильтр по проекту трекера — бейджи выпадали бы на
 * случайных карточках. Придёт с DEV-1475 вместе с GitLab-блоком.
 *
 * Состояния перетаскивания (макет 70:2) — приподнятая под курсором и
 * наклонённая на 2° в захвате — живут в стилях колонки.
 */
import { AppAvatar } from '@vinprop/design-system'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import PriorityGlyph from '@/features/issues/components/PriorityGlyph.vue'
import { formatDueDate } from '@/features/issues/format'
import { typeColor } from '@/features/issues/visuals'

import type { BoardIssue } from '../api'

const props = defineProps<{ issue: BoardIssue }>()

const { locale, t } = useI18n()

const due = computed(() => formatDueDate(props.issue.dueDate, locale.value))

const router = useRouter()
const route = useRoute()

/**
 * Карточку и открывают, и таскают одной и той же кнопкой мыши, поэтому клик
 * считается кликом, только если карточка не уехала: иначе задача открывалась
 * бы после каждого перетаскивания в ту же колонку.
 */
const DRAG_SLOP = 4

const start = { x: 0, y: 0 }

const onDown = (event: MouseEvent) => {
  start.x = event.clientX
  start.y = event.clientY
}

const onClick = (event: MouseEvent) => {
  const moved =
    Math.abs(event.clientX - start.x) > DRAG_SLOP || Math.abs(event.clientY - start.y) > DRAG_SLOP

  if (moved) return

  void router.push({ query: { ...route.query, issue: props.issue.key } })
}
</script>

<template>
  <article
    class="icard"
    role="button"
    tabindex="0"
    :aria-label="t('issues.list.openIssue', { key: issue.key })"
    @mousedown="onDown"
    @click="onClick"
    @keydown.enter="router.push({ query: { ...route.query, issue: issue.key } })"
  >
    <span class="icard__stripe" :style="{ background: typeColor(issue.type) }" aria-hidden="true" />

    <div class="icard__content">
      <div class="icard__head">
        <IssueTypeIcon :type="issue.type" />
        <span class="icard__key">{{ issue.key }}</span>
      </div>

      <h3 class="icard__title">{{ issue.title }}</h3>

      <div class="icard__foot">
        <PriorityGlyph :priority="issue.priority" />
        <span class="icard__spacer" />
        <span v-if="due" class="icard__due">{{ due }}</span>
        <AppAvatar v-if="issue.assigneeName" :name="issue.assigneeName" :size="20" />
      </div>
    </div>
  </article>
</template>

<style scoped>
.icard {
  display: flex;
  align-items: stretch;
  /* Список колонки — колоночный флекс с прокруткой: без этого карточки в нём
     сжимаются под доступную высоту, и в длинной колонке от них остаётся
     полоска в семь пикселей вместо содержимого. */
  flex-shrink: 0;
  /* Полоса типа доходит до края скругления: без обрезки её угол торчит из
     карточки прямоугольником. */
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  cursor: grab;
  /* Иначе перетаскивание начинается с выделения текста и растягивает синеву на
     полдоски: карточку берут целиком, а не читают по словам. */
  user-select: none;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.icard:hover {
  box-shadow: var(--shadow-md);
}

.icard__stripe {
  flex-shrink: 0;
  width: 3px;
}

.icard__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  /* Ширину задаёт колонка: без этого длинный ключ распирает карточку. */
  min-width: 0;
  padding: 10px 12px;
}

.icard__head {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.icard__key {
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  /* Ровно по высоте иконки типа: у моноширинного своя высота строки, и без
     этого шапка карточки то 16, то 17 — карточки в колонке разъезжаются. */
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icard__title {
  /* Две строки и многоточие: карточки в колонке одной высоты, а длинное
     название иначе растягивает одну из них на треть экрана. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  line-height: 20px;
}

.icard__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.icard__spacer {
  flex: 1;
  min-width: 0;
}

.icard__due {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  /* Сроки в колонке стоят друг под другом: без табличных цифр «12 авг» и
     «19 сен» разъезжаются на пиксель-другой и колонка выглядит кривой. */
  font-variant-numeric: tabular-nums;
  line-height: 16px;
  white-space: nowrap;
}
</style>
